import {
  BellOutlined,
  DownOutlined,
  FileSearchOutlined,
  LeftOutlined,
  MailOutlined,
  MessageOutlined,
  PlusOutlined,
  RightOutlined,
  SendOutlined,
  SettingOutlined,
  NotificationOutlined,
  RobotOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Avatar,
  Button,
  ConfigProvider,
  Divider,
  Dropdown,
  Layout,
  Modal,
  Row,
  Skeleton,
  Space,
  Spin,
  Tooltip,
} from "antd";
import { TriggerModal } from "components/triggerModal";
import { useFetchDebtorProfileQuery } from "features/accountsTable/agencyPortal/accountsTableAPI";
import { TableAppearanceModal } from "features/appearance";
import { reconcileColumns } from "features/appearance/components/tableAppearanceModal";
import { SIDER_ITEMS } from "features/appearance/constants";
import { useFetchMeQuery } from "features/auth/authAPI";
import DebtorNavigationArrow from "features/debtorProfile/components/debtorNavigationArrow";
import {
  useFetchDebtorAddressesQuery,
  useFetchDebtorContactsQuery,
  useFetchDebtorEmailsQuery,
  useFetchDebtorEmploymentsQuery,
  useFetchDebtorPhoneNumbersQuery,
} from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { DebtorAddressInfoCard } from "features/debtorSider/components/debtorAddressInfoCard";
import { DebtorContactInfoCard } from "features/debtorSider/components/debtorContactInfoCard";
import { DebtorEmailInfoCard } from "features/debtorSider/components/debtorEmailInfoCard";
import { DebtorEmploymentInfoCard } from "features/debtorSider/components/debtorEmploymentInfoCard";
import { DebtorInfoCard } from "features/debtorSider/components/debtorInfoCard";
import { DebtorNotesCard } from "features/debtorSider/components/debtorNotesCard";
import { DebtorPhoneInfoCard } from "features/debtorSider/components/debtorPhoneInfoCard";
import { DebtorTagsCard } from "features/debtorSider/components/debtorTagsCard";
import PerformSearchModal from "features/debtorSider/components/performSearchModal";
import SendEmailModal from "features/debtorSider/components/sendEmailModal";
import SendTextModal from "features/debtorSider/components/sendTextModal";
import SendVoicemailModal from "features/debtorSider/components/sendVoicemailModal";
import SendLetterSequenceModal from "features/debtorSider/components/sendLetterSequenceModal";
import { PERMISSIONS } from "features/permissions";
import useAuthorizations from "features/permissions/hooks/useAuthorizations";
import { useAppearance } from "providers/appearanceProvider";
import { useCompactMode } from "providers/compactModeProvider";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import CompactSectionWrapper from "features/debtorSider/components/compactSectionWrapper";
import SimilarDebtorsCard from "features/debtorSider/components/similarDebtorsCard";
import CallAiModal from "features/debtorSider/components/callAiModal";
import DebtorRemindersTableModal from "features/debtorSider/components/debtorRemindersTableModal";
import ReplicatePacketedDebtorUpdatesModal from "features/debtorSider/components/replicatePacketedDebtorUpdatesModal";
import PacketedCollectionRecordsModal from "features/debtorProfile/components/packetedCollectionRecordsModal";

const StyledSiderPanel = styled.div`
  position: fixed;
  top: 64px;
  left: 0;
  bottom: 0;
  width: 400px;
  background: #fff;
  display: flex;
  flex-direction: column;
  z-index: 9;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.08);
  transform: translateX(${({ $collapsed }) => ($collapsed ? "-400px" : "0")});
  transition: transform 0.25s ease;
`;

const StyledSiderScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 4px;
`;

const StyledCollapseTab = styled.button`
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  left: ${({ $collapsed }) => ($collapsed ? "0" : "400px")};
  transition: left 0.25s ease;
  z-index: 10;
  background: #fff;
  border: 1px solid #d9d9d9;
  border-left: none;
  border-radius: 0 6px 6px 0;
  padding: 14px 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);

  &:hover {
    background: #f5f5f5;
  }

  .anticon {
    font-size: 11px;
    color: rgba(0, 0, 0, 0.45);
  }
`;

const StyledAvatar = styled(Avatar)`
  background-color: ${({ $isArchived }) => ($isArchived ? "#8c8c8c" : "#0b3948")};
`;

const StyledDebtorName = styled.h2`
  text-align: center;
  text-transform: capitalize;
  margin-left: 8px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  max-width: 220px;
  ${({ $isArchived }) =>
    $isArchived &&
    `
    opacity: 0.45;
    text-decoration: line-through;
  `}
`;

const StyledNavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledProfileContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StyledSpace = styled(Space)`
  margin-top: 8px;
`;

const StyledHeaderName = styled.h4`
  margin: 2px 0 2px 8px;
`;

const StyledEmptySection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: 8px;
  gap: 8px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
`;

const StyledSiderHeader = styled.div`
  flex-shrink: 0;
  background: #fff;
`;

const StyledDivider = styled(Divider)`
  margin-bottom: 0;
  margin-top: 11px;
`;

const StyledCompactDivider = styled(Divider)`
  margin: 8px 0;
`;

const StyledAlert = styled(Alert)`
  padding-top: 6px;
  padding-bottom: 6px;
`;

const StyledArchivedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
`;

const StyledViewMergedButton = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 12px;
`;

const StyledSider = styled(Layout.Sider)`
  padding: 4px;
  overflow: scroll;
  left: 0px;
  top: 216px;
  bottom: 0px;
  &&& {
    position: fixed;
    background: #fff;
  }
`;

const StyledContainer = styled.div`
  position: fixed;
  width: 400px;
  left: 0;
  padding-top: 0px;
  background: #fff;
  min-height: 124px;
`;

const StyledClassicButton = styled(Button)`
  margin-top: 8px;
  margin-left: 8px;
`;

const StyledMoreActionsContainer = styled.div`
  text-align: center;
`;

export const SIDER_ID = "debtorSider";

function DebtorSider({ isSiderCollapsed, onToggleCollapse }) {
  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const { debtorId } = useParams();
  const navigate = useNavigate();
  const { isCompactMode } = useCompactMode();
  const [newPhoneForm, setNewPhoneForm] = useState(null);
  const [newAddressForm, setNewAddressForm] = useState(null);
  const [newEmploymentForm, setNewEmploymentForm] = useState(null);
  const [newEmailForm, setNewEmailForm] = useState(null);
  const [newDebtorContactForm, setNewDebtorContactForm] = useState(null);
  const [replicatePacketedDebtorData, setReplicatePacketedDebtorData] = useState(null);
  const { isLoading: isAppearanceLoading, appearance } = useAppearance();
  useFetchMeQuery();

  const {
    data: debtorPersonalInfo,
    isLoading: isDebtorPersonalInfoLoading,
    isFetching: isDebtorPersonalInfoFetching,
    isError: isDebtorPersonalInfoError,
  } = useFetchDebtorProfileQuery({ debtorId });
  const { data: debtorAddresses, isLoading: isDebtorAddressesLoading } =
    useFetchDebtorAddressesQuery(
      { debtorId },
      {
        skip:
          isDebtorPersonalInfoLoading || isDebtorPersonalInfoError || isDebtorPersonalInfoFetching,
      },
    );
  const { data: debtorContacts, isLoading: isDebtorContactsLoading } = useFetchDebtorContactsQuery(
    {
      debtorId,
    },
    {
      skip:
        isDebtorPersonalInfoLoading || isDebtorPersonalInfoError || isDebtorPersonalInfoFetching,
    },
  );
  const { data: debtorEmails, isLoading: isDebtorEmailsLoading } = useFetchDebtorEmailsQuery(
    {
      debtorId,
    },
    {
      skip:
        isDebtorPersonalInfoLoading || isDebtorPersonalInfoError || isDebtorPersonalInfoFetching,
    },
  );
  const { data: debtorPhoneNumbers, isLoading: isDebtorPhoneNumbersLoading } =
    useFetchDebtorPhoneNumbersQuery(
      { debtorId },
      {
        skip:
          isDebtorPersonalInfoLoading || isDebtorPersonalInfoError || isDebtorPersonalInfoFetching,
      },
    );
  const { data: debtorEmployments, isLoading: isDebtorEmploymentsLoading } =
    useFetchDebtorEmploymentsQuery(
      { debtorId },
      {
        skip:
          isDebtorPersonalInfoLoading || isDebtorPersonalInfoError || isDebtorPersonalInfoFetching,
      },
    );

  const {
    data: {
      [PERMISSIONS.PHONE__CALL_DEBTOR]: isPhoneAuthorized,
      [PERMISSIONS.SKIP_TRACE__START]: isSkipTraceAuthorized,
      [PERMISSIONS.DEBTOR_ADDRESS__CREATE]: isAddDebtorAddressAuthorized,
      [PERMISSIONS.DEBTOR_CONTACT__CREATE]: isAddDebtorContactAuthorized,
      [PERMISSIONS.DEBTOR_EMPLOYMENT__CREATE]: isAddDebtorEmploymentAuthorized,
      [PERMISSIONS.DEBTOR_PHONE_NUMBER__CREATE]: isAddDebtorPhoneNumberAuthorized,
      [PERMISSIONS.DEBTOR_EMAIL__CREATE]: isAddDebtorEmailAuthorized,
      [PERMISSIONS.DEBTOR_ADDRESS__GET]: isGetDebtorAddressAuthorized,
      [PERMISSIONS.DEBTOR_CONTACT__GET]: isGetDebtorContactAuthorized,
      [PERMISSIONS.DEBTOR_EMPLOYMENT__GET]: isGetDebtorEmploymentAuthorized,
      [PERMISSIONS.DEBTOR_PHONE_NUMBER__GET]: isGetDebtorPhoneNumberAuthorized,
      [PERMISSIONS.DEBTOR_EMAIL__GET]: isGetDebtorEmailAuthorized,
      [PERMISSIONS.LETTER__SEND]: isLetterAuthorized,
      [PERMISSIONS.EMAIL__SEND]: isEmailAuthorized,
      [PERMISSIONS.TEXT_MESSAGE__SEND]: isTextAuthorized,
      [PERMISSIONS.TEXT_MESSAGE__SEND]: isVoiceMailAuthorized, // TODO: Add specific permission for voicemail
      [PERMISSIONS.APPEARANCE__UPDATE]: isAppearanceUpdateAuthorized,
      [PERMISSIONS.DEBTOR_PACKET__CREATE]: isDebtorPacketCreateAuthorized,
      isLoadingPermissionSummary,
    },
  } = useAuthorizations();

  const isLoading =
    isDebtorPersonalInfoLoading ||
    isDebtorAddressesLoading ||
    isDebtorContactsLoading ||
    isDebtorPhoneNumbersLoading ||
    isDebtorEmploymentsLoading ||
    isDebtorEmailsLoading ||
    isAppearanceLoading ||
    isLoadingPermissionSummary;

  const debtorDisplayName = [
    debtorPersonalInfo?.firstName,
    debtorPersonalInfo?.middleName,
    debtorPersonalInfo?.lastName,
  ]
    .filter((x) => x)
    .join(" ");

  const handleCreatePhone = async () => {
    setNewPhoneForm(null);
  };
  const onCancelCreatePhone = () => {
    setNewPhoneForm(null);
  };
  const onAddPhoneClick = () => {
    setNewPhoneForm(
      <DebtorPhoneInfoCard
        triggerReplicatePacketedDebtorUpdatesModal={(data) => {
          setReplicatePacketedDebtorData(data);
        }}
        isNew
        onCreate={handleCreatePhone}
        onCancel={onCancelCreatePhone}
      />,
    );
  };

  const handleCreateAddress = async () => {
    setNewAddressForm(null);
  };
  const onCancelCreateAddress = () => {
    setNewAddressForm(null);
  };
  const onAddAddressClick = () => {
    setNewAddressForm(
      <DebtorAddressInfoCard
        triggerReplicatePacketedDebtorUpdatesModal={(data) => {
          setReplicatePacketedDebtorData(data);
        }}
        isNew
        onCreate={handleCreateAddress}
        onCancel={onCancelCreateAddress}
      />,
    );
  };

  const handleCreateEmail = async () => {
    setNewEmailForm(null);
  };
  const onCancelCreateEmail = () => {
    setNewEmailForm(null);
  };
  const onAddEmailClick = () => {
    setNewEmailForm(
      <DebtorEmailInfoCard
        triggerReplicatePacketedDebtorUpdatesModal={(data) => {
          setReplicatePacketedDebtorData(data);
        }}
        isNew
        onCreate={handleCreateEmail}
        onCancel={onCancelCreateEmail}
      />,
    );
  };

  const handleCreateEmployment = async () => {
    setNewEmploymentForm(null);
  };
  const onCancelCreateEmployment = () => setNewEmploymentForm(null);
  const onAddEmploymentClick = () =>
    setNewEmploymentForm(
      <DebtorEmploymentInfoCard
        triggerReplicatePacketedDebtorUpdatesModal={(data) => {
          setReplicatePacketedDebtorData(data);
        }}
        isNew
        onCreate={handleCreateEmployment}
        onCancel={onCancelCreateEmployment}
      />,
    );

  const handleCreateDebtorContact = async () => {
    setNewDebtorContactForm(null);
  };
  const onCancelCreateDebtorContact = () => setNewDebtorContactForm(null);
  const onAddDebtorContactClick = () => {
    setNewDebtorContactForm(
      <DebtorContactInfoCard
        triggerReplicatePacketedDebtorUpdatesModal={(data) => {
          setReplicatePacketedDebtorData(data);
        }}
        isNew
        profileType={debtorPersonalInfo?.type}
        onCreate={handleCreateDebtorContact}
        onCancel={onCancelCreateDebtorContact}
      />,
    );
  };

  const emailOnOk = async () => {
    setModalContent(null);
    setModalTitle("");
  };

  const aiDialerOnOk = async () => {
    setModalContent(null);
    setModalTitle("");
  };

  const letterOnOk = async () => {
    setModalContent(null);
    setModalTitle("");
  };

  const voicemailOnOk = async () => {
    setModalContent(null);
    setModalTitle("");
  };

  const onCancel = () => {
    setModalContent(null);
    setModalTitle("");
  };

  const showModalContentFor = (key, title) => {
    setModalTitle(title);
    if (key === "letter") {
      setModalContent(
        <SendLetterSequenceModal
          title="Send Letter"
          open={modalContent || key}
          onOk={letterOnOk}
          onCancel={onCancel}
        />,
      );
    } else if (key === "phone") {
      setModalContent(
        <Modal
          title="Phone"
          open={modalContent || key}
          onOk={() => {
            setModalContent(null);
            setModalTitle("");
          }}
          onCancel={onCancel}
        >
          Coming Soon
        </Modal>,
      );
    } else if (key === "text") {
      setModalContent(
        <Modal
          title="Text Message"
          open={modalContent || key}
          onOk={() => {
            setModalContent(null);
            setModalTitle("");
          }}
          onCancel={onCancel}
        >
          <SendTextModal
            title="Send Text Message"
            open={modalContent || key}
            onOk={emailOnOk}
            onCancel={onCancel}
          />
        </Modal>,
      );
    } else if (key === "voicemail") {
      setModalContent(
        <Modal
          title="Voicemail"
          open={modalContent || key}
          onOk={() => {
            setModalContent(null);
            setModalTitle("");
          }}
          onCancel={onCancel}
        >
          <SendVoicemailModal
            title="Send Voicemail"
            open={modalContent || key}
            onOk={voicemailOnOk}
            onCancel={onCancel}
          />
        </Modal>,
      );
    } else if (key === "email") {
      setModalContent(
        <SendEmailModal
          title="Send Email"
          open={modalContent || key}
          onOk={emailOnOk}
          onCancel={onCancel}
        />,
      );
    } else if (key === "aiDialer") {
      setModalContent(
        <CallAiModal
          title="Call With Aktos AI"
          open={modalContent || key}
          onOk={aiDialerOnOk}
          onCancel={onCancel}
        />,
      );
    } else if (key === "creditor") {
      setModalContent(
        <Modal
          title="Phone"
          open={modalContent || key}
          onOk={() => {
            setModalContent(null);
            setModalTitle("");
          }}
          onCancel={onCancel}
        >
          Coming Soon
        </Modal>,
      );
    } else if (key === "search") {
      setModalContent(
        <PerformSearchModal
          title={modalTitle}
          open={modalContent || key}
          onOk={() => {
            setModalContent(null);
            setModalTitle("");
          }}
          onCancel={onCancel}
        />,
      );
    }
  };

  const moreActionsItems = [
    ...(!debtorPersonalInfo?.packetId && isDebtorPacketCreateAuthorized
      ? [
          {
            key: "createPacket",
            label: "Create a Packet",
            onClick: () =>
              setModalContent(
                <PacketedCollectionRecordsModal
                  packetId={debtorPersonalInfo?.packetId}
                  open
                  onCancel={() => {
                    setModalContent(null);
                    setModalTitle("");
                  }}
                />,
              ),
          },
        ]
      : []),
  ];

  const defaultItems = [
    {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_NOTES,
      visible: true,
      title: "Notes",
      render: () => <DebtorNotesCard />,
    },
    {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_TAGS,
      visible: true,
      title: "Tags",
      render: () => <DebtorTagsCard />,
    },

    {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_INFO,
      visible: true,
      title: "Debtor Info",
      render: () => (
        <DebtorInfoCard
          triggerReplicatePacketedDebtorUpdatesModal={(data) => {
            setReplicatePacketedDebtorData(data);
          }}
          profileType={debtorPersonalInfo?.type}
        />
      ),
    },
    isGetDebtorPhoneNumberAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__PHONE_NUMBERS,
      visible: true,
      title: "Phone Numbers",
      render: (item) => (
        <span key={item.key}>
          {isCompactMode ? (
            <CompactSectionWrapper
              items={[...(debtorPhoneNumbers ?? [])].sort(
                (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0),
              )}
              entityName="phone"
              renderItem={(phone, index) => (
                <DebtorPhoneInfoCard
                  triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                    setReplicatePacketedDebtorData(data);
                  }}
                  key={index}
                  phone={phone}
                  profileType={debtorPersonalInfo?.type}
                  title="Phone"
                  isFirstItem={index === 0}
                />
              )}
              addButton={
                !newPhoneForm &&
                isAddDebtorPhoneNumberAuthorized &&
                debtorPhoneNumbers?.length > 0 ? (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={onAddPhoneClick}
                  >
                    Add
                  </Button>
                ) : null
              }
            />
          ) : (
            debtorPhoneNumbers?.map((phone, index) => (
              <DebtorPhoneInfoCard
                triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                  setReplicatePacketedDebtorData(data);
                }}
                key={index}
                phone={phone}
                profileType={debtorPersonalInfo?.type}
                title={index === 0 ? "Phone" : `Phone (${index + 1})`}
              />
            ))
          )}
          {newPhoneForm ||
            (isCompactMode ? (
              debtorPhoneNumbers?.length === 0 && (
                <>
                  <StyledHeaderName>Phone Numbers</StyledHeaderName>
                  <StyledEmptySection>
                    There are currently no phone numbers.
                    {isAddDebtorPhoneNumberAuthorized && (
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={onAddPhoneClick}
                      >
                        Add
                      </Button>
                    )}
                  </StyledEmptySection>
                </>
              )
            ) : (
              <>
                {debtorPhoneNumbers?.length === 0 && <StyledHeaderName>Phones</StyledHeaderName>}
                {isAddDebtorPhoneNumberAuthorized && (
                  <StyledClassicButton
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={onAddPhoneClick}
                  >
                    Add Phone number
                  </StyledClassicButton>
                )}
              </>
            ))}
        </span>
      ),
    },
    isGetDebtorEmailAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_EMAILS,
      visible: true,
      title: "Emails",
      render: (item) => (
        <span key={item.key}>
          {isCompactMode ? (
            <CompactSectionWrapper
              items={[...(debtorEmails ?? [])].sort(
                (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0),
              )}
              entityName="email"
              renderItem={(email, index) => (
                <DebtorEmailInfoCard
                  triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                    setReplicatePacketedDebtorData(data);
                  }}
                  key={email.id}
                  email={email}
                  title="Email"
                  isFirstItem={index === 0}
                />
              )}
              addButton={
                !newEmailForm && isAddDebtorEmailAuthorized && debtorEmails?.length > 0 ? (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={onAddEmailClick}
                  >
                    Add
                  </Button>
                ) : null
              }
            />
          ) : (
            debtorEmails?.map((email, index) => (
              <DebtorEmailInfoCard
                triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                  setReplicatePacketedDebtorData(data);
                }}
                key={email.id}
                email={email}
                title={index === 0 ? "Email" : `Email (${index + 1})`}
              />
            ))
          )}
          {newEmailForm ||
            (isCompactMode ? (
              debtorEmails?.length === 0 && (
                <>
                  <StyledHeaderName>Emails</StyledHeaderName>
                  <StyledEmptySection>
                    There are currently no emails.
                    {isAddDebtorEmailAuthorized && (
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={onAddEmailClick}
                      >
                        Add
                      </Button>
                    )}
                  </StyledEmptySection>
                </>
              )
            ) : (
              <>
                {debtorEmails?.length === 0 && <StyledHeaderName>Email</StyledHeaderName>}
                {isAddDebtorEmailAuthorized && (
                  <StyledClassicButton
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={onAddEmailClick}
                  >
                    Add Email
                  </StyledClassicButton>
                )}
              </>
            ))}
        </span>
      ),
    },
    isGetDebtorAddressAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_ADDRESSES,
      visible: true,
      title: "Addresses",
      render: (item) => (
        <span key={item.key}>
          {isCompactMode ? (
            <CompactSectionWrapper
              items={[...(debtorAddresses ?? [])].sort(
                (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0),
              )}
              entityName="address"
              renderItem={(address, index) => (
                <DebtorAddressInfoCard
                  triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                    setReplicatePacketedDebtorData(data);
                  }}
                  key={index}
                  profileType={debtorPersonalInfo?.type}
                  address={address}
                  title="Address"
                  isFirstItem={index === 0}
                />
              )}
              addButton={
                !newAddressForm && isAddDebtorAddressAuthorized && debtorAddresses?.length > 0 ? (
                  <Button
                    type="primary"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={onAddAddressClick}
                  >
                    Add
                  </Button>
                ) : null
              }
            />
          ) : (
            debtorAddresses?.map((address, index) => (
              <DebtorAddressInfoCard
                triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                  setReplicatePacketedDebtorData(data);
                }}
                key={index}
                profileType={debtorPersonalInfo?.type}
                address={address}
                title={index === 0 ? "Address" : `Address (${index + 1})`}
              />
            ))
          )}
          {newAddressForm ||
            (isCompactMode ? (
              debtorAddresses?.length === 0 && (
                <>
                  <StyledHeaderName>Addresses</StyledHeaderName>
                  <StyledEmptySection>
                    There are currently no addresses.
                    {isAddDebtorAddressAuthorized && (
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={onAddAddressClick}
                      >
                        Add
                      </Button>
                    )}
                  </StyledEmptySection>
                </>
              )
            ) : (
              <>
                {debtorAddresses?.length === 0 && <StyledHeaderName>Address</StyledHeaderName>}
                {isAddDebtorAddressAuthorized && (
                  <StyledClassicButton
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={onAddAddressClick}
                  >
                    Add Address
                  </StyledClassicButton>
                )}
              </>
            ))}
        </span>
      ),
    },
    isGetDebtorContactAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_CONTACTS,
      visible: true,
      title: "Contacts",
      render: (item) => {
        const filteredContacts =
          debtorContacts?.filter(
            (contact) => contact.relationshipToDebtor !== "debtor" && !contact.isArchived,
          ) ?? [];
        const contactLabel =
          debtorPersonalInfo?.type === "consumer" ? "Debtor Contact" : "Contact Person";
        return (
          <span key={item.key}>
            {isCompactMode ? (
              <CompactSectionWrapper
                items={filteredContacts}
                entityName="contact"
                renderItem={(debtorContact, index) => (
                  <DebtorContactInfoCard
                    triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                      setReplicatePacketedDebtorData(data);
                    }}
                    key={index}
                    profileType={debtorPersonalInfo?.type}
                    debtorContact={debtorContact}
                    title={contactLabel}
                    isFirstItem={index === 0}
                  />
                )}
                addButton={
                  !newDebtorContactForm &&
                  isAddDebtorContactAuthorized &&
                  filteredContacts.length > 0 ? (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={onAddDebtorContactClick}
                    >
                      Add
                    </Button>
                  ) : null
                }
              />
            ) : (
              debtorContacts
                ?.filter(
                  (contact) => contact.relationshipToDebtor !== "debtor" && !contact.isArchived,
                )
                ?.map((debtorContact, index) => (
                  <DebtorContactInfoCard
                    triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                      setReplicatePacketedDebtorData(data);
                    }}
                    key={index}
                    profileType={debtorPersonalInfo?.type}
                    debtorContact={debtorContact}
                    title={index === 0 ? contactLabel : `${contactLabel} (${index + 1})`}
                  />
                ))
            )}
            {newDebtorContactForm ||
              (isCompactMode ? (
                filteredContacts.length === 0 && (
                  <>
                    <StyledHeaderName>{contactLabel}</StyledHeaderName>
                    <StyledEmptySection>
                      There are currently no {contactLabel.toLowerCase()}s.
                      {isAddDebtorContactAuthorized && (
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={onAddDebtorContactClick}
                        >
                          Add
                        </Button>
                      )}
                    </StyledEmptySection>
                  </>
                )
              ) : (
                <>
                  {filteredContacts.length === 0 && (
                    <StyledHeaderName>{contactLabel}</StyledHeaderName>
                  )}
                  {isAddDebtorContactAuthorized && (
                    <StyledClassicButton
                      type="primary"
                      onClick={onAddDebtorContactClick}
                      icon={<PlusOutlined />}
                    >
                      Add {contactLabel}
                    </StyledClassicButton>
                  )}
                </>
              ))}
          </span>
        );
      },
    },
    isGetDebtorEmploymentAuthorized && {
      key: SIDER_ITEMS.DEBTOR_SIDER__DEBTOR_EMPLOYMENTS,
      visible: true,
      title: "Employments",
      render: (item) => (
        <span key={item.key}>
          {debtorPersonalInfo?.type === "consumer" &&
            (isCompactMode ? (
              <CompactSectionWrapper
                items={[...(debtorEmployments ?? [])].sort(
                  (a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0),
                )}
                entityName="employment"
                renderItem={(employment, index) => (
                  <DebtorEmploymentInfoCard
                    triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                      setReplicatePacketedDebtorData(data);
                    }}
                    key={index}
                    employment={employment}
                    title="Employment"
                    isFirstItem={index === 0}
                  />
                )}
                addButton={
                  !newEmploymentForm &&
                  isAddDebtorEmploymentAuthorized &&
                  debtorEmployments?.length > 0 ? (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={onAddEmploymentClick}
                    >
                      Add
                    </Button>
                  ) : null
                }
              />
            ) : (
              debtorEmployments?.map((employment, index) => (
                <DebtorEmploymentInfoCard
                  triggerReplicatePacketedDebtorUpdatesModal={(data) => {
                    setReplicatePacketedDebtorData(data);
                  }}
                  key={index}
                  employment={employment}
                  title={index === 0 ? "Employment" : `Employment (${index + 1})`}
                />
              ))
            ))}
          {newEmploymentForm ||
            (debtorPersonalInfo?.type === "consumer" &&
              (isCompactMode ? (
                debtorEmployments?.length === 0 && (
                  <>
                    <StyledHeaderName>Employment</StyledHeaderName>
                    <StyledEmptySection>
                      There are currently no employments.
                      {isAddDebtorEmploymentAuthorized && (
                        <Button
                          type="primary"
                          size="small"
                          icon={<PlusOutlined />}
                          onClick={onAddEmploymentClick}
                        >
                          Add
                        </Button>
                      )}
                    </StyledEmptySection>
                  </>
                )
              ) : (
                <>
                  {debtorEmployments?.length === 0 && (
                    <StyledHeaderName>Employment</StyledHeaderName>
                  )}
                  {isAddDebtorEmploymentAuthorized && (
                    <StyledClassicButton
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={onAddEmploymentClick}
                    >
                      Add Employment
                    </StyledClassicButton>
                  )}
                </>
              )))}
        </span>
      ),
    },
  ].filter(Boolean); // .filter(Boolean) removes falsy values (null, undefined, false, 0, "")

  const reconciledItems = reconcileColumns(defaultItems, appearance?.items?.[SIDER_ID]).filter(
    (column) => column.visible,
  );

  if (
    appearance?.items?.[SIDER_ID]?.hidePanel || // Hiding panel in appearances
    isAppearanceLoading || // Appearance is still loading
    isDebtorPersonalInfoError // Debtor personal info failed to load
  ) {
    return null;
  }

  const sharedHeader = !isDebtorPersonalInfoLoading && !isDebtorPersonalInfoError && (
    <StyledNavigationContainer>
      <DebtorNavigationArrow direction="left" disabled={isLoading} />
      <StyledProfileContainer>
        <Row align="middle" wrap={false}>
          <Tooltip title={debtorPersonalInfo?.mergeParentId ? "Archived" : undefined}>
            <StyledAvatar size={40} $isArchived={!!debtorPersonalInfo?.mergeParentId}>
              {(debtorDisplayName || debtorPersonalInfo?.companyName)?.split(" ").map((c) => c[0])}
            </StyledAvatar>
          </Tooltip>
          <Tooltip title={debtorDisplayName || debtorPersonalInfo?.companyName} placement="right">
            <StyledDebtorName $isArchived={!!debtorPersonalInfo?.mergeParentId}>
              {(debtorDisplayName || debtorPersonalInfo?.companyName)?.toLowerCase()}
            </StyledDebtorName>
          </Tooltip>
          {isAppearanceUpdateAuthorized && (
            <TriggerModal
              modal={TableAppearanceModal}
              tableId={SIDER_ID}
              defaultColumns={defaultItems}
              mode="sider"
            >
              <Tooltip title="Customize Appearance">
                <Button shape="circle" icon={<SettingOutlined />} type="text" />
              </Tooltip>
            </TriggerModal>
          )}
        </Row>
        {debtorPersonalInfo?.mergeParentId && (
          <StyledArchivedRow>
            <StyledViewMergedButton
              type="link"
              size="small"
              onClick={() => navigate(`/debtors/${debtorPersonalInfo.mergeParentId}`)}
            >
              View merged debtor
            </StyledViewMergedButton>
          </StyledArchivedRow>
        )}
        <StyledSpace>
          <Tooltip title="Manage Reminders">
            <Button
              type="primary"
              shape="circle"
              icon={<BellOutlined />}
              onClick={() =>
                setModalContent(
                  <DebtorRemindersTableModal
                    open
                    onOk={() => {
                      setModalContent(null);
                      setModalTitle("");
                    }}
                    onCancel={() => {
                      setModalContent(null);
                      setModalTitle("");
                    }}
                  />,
                )
              }
            />
          </Tooltip>
          {isEmailAuthorized && (
            <Tooltip title="Email">
              <Button
                type="primary"
                shape="circle"
                icon={<MailOutlined />}
                onClick={() => showModalContentFor("email")}
              />
            </Tooltip>
          )}
          {isLetterAuthorized && (
            <Tooltip title="Letter">
              <Button
                type="primary"
                shape="circle"
                icon={<SendOutlined />}
                onClick={() => showModalContentFor("letter")}
              />
            </Tooltip>
          )}
          {isTextAuthorized && (
            <Tooltip title="Text Message">
              <Button
                type="primary"
                shape="circle"
                icon={<MessageOutlined />}
                onClick={() => showModalContentFor("text")}
              />
            </Tooltip>
          )}
          {isVoiceMailAuthorized && (
            <Tooltip title="Voicemail">
              <Button
                type="primary"
                shape="circle"
                icon={<NotificationOutlined />}
                onClick={() => showModalContentFor("voicemail")}
              />
            </Tooltip>
          )}
          {isPhoneAuthorized && (
            <Tooltip title="AI Phone Agent">
              <Button
                type="primary"
                shape="circle"
                icon={<RobotOutlined />}
                onClick={() => showModalContentFor("aiDialer")}
              />
            </Tooltip>
          )}
          {isSkipTraceAuthorized && (
            <Tooltip title="Search on debtor">
              <Button
                type="primary"
                shape="circle"
                icon={<FileSearchOutlined />}
                onClick={() => showModalContentFor("search")}
              />
            </Tooltip>
          )}
          {isCompactMode && moreActionsItems.length > 0 && (
            <Dropdown menu={{ items: moreActionsItems }} trigger={["click"]}>
              <Tooltip title="More Actions">
                <Button type="primary" shape="circle" icon={<EllipsisOutlined />} />
              </Tooltip>
            </Dropdown>
          )}
        </StyledSpace>
      </StyledProfileContainer>
      <DebtorNavigationArrow direction="right" disabled={isLoading} />
    </StyledNavigationContainer>
  );

  const sharedContent = (
    <>
      {debtorPersonalInfo?.isDebtorAvailableToContact === false && (
        <StyledAlert
          message="DO NOT CALL"
          description="Note: Address and/or Phone is out of timezone"
          type="error"
          showIcon
        />
      )}
      <SimilarDebtorsCard currentDebtor={debtorPersonalInfo} />
      {reconciledItems.map((item, idx) => (
        <div key={idx}>
          {item.render(item)}
          {isCompactMode ? <StyledCompactDivider /> : <StyledDivider />}
        </div>
      ))}
      {modalContent}
    </>
  );

  if (isCompactMode) {
    return (
      <ConfigProvider
        componentSize="small"
        theme={{
          token: {
            controlHeight: 26,
            controlHeightSM: 20,
            controlHeightLG: 32,
            fontSize: 12,
            fontSizeSM: 10,
          },
          components: {
            Button: {
              contentFontSize: 11,
              contentFontSizeSM: 10,
              contentFontSizeLG: 12,
              onlyIconSize: 11,
              onlyIconSizeSM: 10,
              onlyIconSizeLG: 13,
              paddingInline: 10,
              paddingInlineSM: 7,
            },
          },
        }}
      >
        <StyledCollapseTab
          $collapsed={isSiderCollapsed}
          onClick={onToggleCollapse}
          title={isSiderCollapsed ? "Expand panel" : "Collapse panel"}
        >
          {isSiderCollapsed ? <RightOutlined /> : <LeftOutlined />}
        </StyledCollapseTab>

        <StyledSiderPanel $collapsed={isSiderCollapsed}>
          <StyledSiderHeader>
            {sharedHeader}
            <StyledDivider />
          </StyledSiderHeader>

          <StyledSiderScrollArea>
            {isLoading ? (
              <Skeleton active />
            ) : (
              <Spin spinning={isDebtorPersonalInfoFetching}>{sharedContent}</Spin>
            )}
          </StyledSiderScrollArea>
        </StyledSiderPanel>

        {replicatePacketedDebtorData && (
          <ReplicatePacketedDebtorUpdatesModal
            replicatePacketedDebtorData={replicatePacketedDebtorData}
            onCancel={() => setReplicatePacketedDebtorData(null)}
            packetId={debtorPersonalInfo?.packetId}
            onOk={() => setReplicatePacketedDebtorData(null)}
          />
        )}
      </ConfigProvider>
    );
  }

  return (
    <>
      <StyledContainer>
        {sharedHeader}
        {moreActionsItems.length > 0 && (
          <StyledMoreActionsContainer>
            <Dropdown menu={{ items: moreActionsItems }} trigger={["click"]}>
              <Button type="link">
                More Actions <DownOutlined />
              </Button>
            </Dropdown>
          </StyledMoreActionsContainer>
        )}
        <StyledDivider />
      </StyledContainer>
      <StyledSider width={400} theme="light">
        {isLoading ? (
          <Skeleton active />
        ) : (
          <Spin spinning={isDebtorPersonalInfoFetching}>{sharedContent}</Spin>
        )}
      </StyledSider>
      {replicatePacketedDebtorData && (
        <ReplicatePacketedDebtorUpdatesModal
          replicatePacketedDebtorData={replicatePacketedDebtorData}
          onCancel={() => setReplicatePacketedDebtorData(null)}
          packetId={debtorPersonalInfo?.packetId}
          onOk={() => setReplicatePacketedDebtorData(null)}
        />
      )}
    </>
  );
}

export default DebtorSider;
